import * as React from 'react'
import { useEffect } from 'react'
import { PageDefinition, PageType } from '../pageDefinition'
import { Box, Grid, Loader } from 'cs-kevlar-react'
import { LicensedAgencyConfigurationV1, WebAdminUser } from '../../api'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import {
  createStyles,
  makeStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import { SidePanelContent } from 'components'
import { useAgencyApi, useAuth, useInvokeApi } from 'hooks'
import { canViewAgencyPage, isAssisting } from 'utils'
import { usePageViewAudit } from 'hooks/usePageViewAudit'
import {
  ConfigurationCard,
  ConfigurationGroup,
} from 'components/agencies/ConfigurationCard'
import { EditDeviceConfiguration } from 'components/agencies/EditDeviceConfiguration'
import { AsyncPayload } from 'components/base'
import { EditNotificationConfiguration } from 'components/agencies/EditNotificationConfiguration'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    contentBox: {},
    spacer: {
      flexGrow: 1,
      minHeight: 0,
    },
    gridContainer: {
      marginBottom: theme.spacing(8),
    },
  }),
)

const buildPageTitle = (user: WebAdminUser | null) => {
  if (!user?.licensedAgencyName) return 'Configuration'
  return `${user.licensedAgencyName} - Configuration`
}

const getConfigurationGroupFromRoute = (route: String) => {
  if (route.endsWith('devices')) return ConfigurationGroup.Devices
  return ConfigurationGroup.Root
}

const buildNavigationUrl = (
  configurationGroup: ConfigurationGroup,
  isAssisting: boolean,
  agency: AssistedAgency | null,
) => {
  if (!agency || !isAssisting) {
    if (configurationGroup === ConfigurationGroup.Devices)
      return '/configuration/devices'
    return '/configuration'
  }

  if (configurationGroup === ConfigurationGroup.Devices)
    return `/assist/customer/${agency.customerAgencyId}/agency/${agency.licensedAgencyId}/configuration/devices`

  return `/assist/customer/${agency.customerAgencyId}/agency/${agency.licensedAgencyId}/configuration`
}

const buildDeviceSessionLockTimeoutValueDescription = (
  configuration: LicensedAgencyConfigurationV1,
) => {
  if (configuration?.deviceSessionLockTimeoutInMinutes === null)
    return 'Not set'

  if (configuration.deviceSessionLockTimeoutInMinutes === 1)
    return `${configuration.deviceSessionLockTimeoutInMinutes} minute`

  return `${configuration.deviceSessionLockTimeoutInMinutes} minutes`
}

const buildSignificantUnitsAssignedValueDescription = (
  configuration: LicensedAgencyConfigurationV1,
) => {
  if (
    configuration?.incidentNotificationSignificantUnitsAssigned == null ||
    configuration?.incidentNotificationSignificantUnitsAssigned < 2
  )
    return 'Off'

  return `${configuration.incidentNotificationSignificantUnitsAssigned} units`
}

const buildIncidentHighPrioritiesValueDescription = (
  configuration: LicensedAgencyConfigurationV1,
) => {
  if (
    configuration?.incidentNotificationHighPriorities == null ||
    configuration?.incidentNotificationHighPriorities.length <= 0
  )
    return 'Off'

  return `${
    configuration.incidentNotificationHighPriorities.length + ' selected'
  }`
}

const buildIncidentStatusesValueDescription = (
  configuration: LicensedAgencyConfigurationV1,
) => {
  if (
    configuration?.incidentNotificationStatuses == null ||
    configuration?.incidentNotificationStatuses.length <= 0
  )
    return 'Off'

  return `${configuration.incidentNotificationStatuses.length + ' selected'}`
}

const buildUnitStatusesValueDescription = (
    configuration: LicensedAgencyConfigurationV1,
) => {
  if (
      configuration?.emergencyResourceNotificationStatuses == null ||
      configuration?.emergencyResourceNotificationStatuses.length <= 0
  )
    return 'Off'

  return `${configuration.emergencyResourceNotificationStatuses.length + ' selected'}`
}

type ConfigurationProps = {
  customerAgencyId: string
  licensedAgencyId: string
}

type AssistedAgency = {
  customerAgencyId: string
  licensedAgencyId: string
}

const Configuration: React.FunctionComponent = () => {

  const params = useParams<ConfigurationProps>()

  const classes = useStyles()
  const invokeApi = useInvokeApi()
  const navigate = useNavigate()
  const location = useLocation()
  const { user: authUser } = useAuth()
  const { getLicensedAgencyConfiguration } = useAgencyApi()
  const [configurationGroup, setConfigurationGroup] = React.useState<
    ConfigurationGroup
  >(getConfigurationGroupFromRoute(location.pathname))
  const currentLicensedAgencyId =
    params.licensedAgencyId ?? authUser?.licensedAgencyId ?? ''
  const [
    licensedAgencyConfiguration,
    setLicensedAgencyConfiguration,
  ] = React.useState<AsyncPayload<LicensedAgencyConfigurationV1>>({
    payload: null,
    isLoading: true,
  })

  useEffect(() => {
    if (
      authUser === null ||
      !authUser.customerAgencyId ||
      !authUser.licensedAgencyId
    )
      return navigate(buildNavigationUrl(configurationGroup, false, null))

    navigate(
      buildNavigationUrl(configurationGroup, isAssisting(authUser), {
        customerAgencyId: authUser.customerAgencyId,
        licensedAgencyId: authUser.licensedAgencyId,
      }),
    )
  }, [location.pathname, configurationGroup])

  useEffect(() => {
    const loadData = async () => {
      await invokeApi(
        () => {
          return getLicensedAgencyConfiguration(currentLicensedAgencyId)
        },
        'There was an error getting the configuration.',
        (result) => {
          setLicensedAgencyConfiguration({
            payload: result,
            isLoading: false,
          })
        },
      )
    }

    loadData()
  }, [configurationGroup])

  usePageViewAudit(buildPageTitle(authUser), !!authUser)

  return (
    <React.Fragment>
      {licensedAgencyConfiguration.isLoading && (
        <Loader size="sm" text="Loading Configuration" />
      )}
      {licensedAgencyConfiguration.payload !== null && (
        <Box>
          <Grid
            container
            direction="row"
            alignItems="flex-start"
            justifyContent="flex-start"
            className={classes.gridContainer}
          >
            <Grid item>
              <ConfigurationCard
                key="deviceSessionLockTimeoutInMinutes"
                title="Devices"
                description="Agency settings for devices"
                settings={[
                  {
                    name: 'Session Lock Timeout',
                    value: buildDeviceSessionLockTimeoutValueDescription(
                      licensedAgencyConfiguration.payload,
                    ),
                  },
                ]}
                group={ConfigurationGroup.Devices}
                onEdit={setConfigurationGroup}
              />
            </Grid>
            <Grid item>
              <ConfigurationCard
                key="deviceSessionLockTimeoutInMinutes"
                title="App Notifications"
                description="Agency settings for app notifications"
                settings={[
                  {
                    name: 'Several Units Assigned to a Call',
                    value: buildSignificantUnitsAssignedValueDescription(
                      licensedAgencyConfiguration.payload,
                    ),
                  },
                  {
                    name: 'High Priorities',
                    value: buildIncidentHighPrioritiesValueDescription(
                      licensedAgencyConfiguration.payload,
                    ),
                  },
                  {
                    name: 'Incident Statuses',
                    value: buildIncidentStatusesValueDescription(
                      licensedAgencyConfiguration.payload,
                    ),
                  },
                  {
                    name: 'Unit Statuses',
                    value: buildUnitStatusesValueDescription(
                        licensedAgencyConfiguration.payload,
                    ),
                  },
                ]}
                group={ConfigurationGroup.Notifications}
                onEdit={setConfigurationGroup}
              />
            </Grid>
          </Grid>
        </Box>
      )}
      {configurationGroup === ConfigurationGroup.Devices && (
        <SidePanelContent
          anchor="right"
          title="Devices"
          auditTitle={`${buildPageTitle(authUser)} / Devices`}
          isReadyToAudit={!!authUser}
          onClose={() => setConfigurationGroup(ConfigurationGroup.Root)}
        >
          <EditDeviceConfiguration
            onClose={() => setConfigurationGroup(ConfigurationGroup.Root)}
            licensedAgencyId={currentLicensedAgencyId}
          />
        </SidePanelContent>
      )}
      {configurationGroup === ConfigurationGroup.Notifications && (
        <SidePanelContent
          anchor="right"
          title="App Notifications"
          auditTitle={`${buildPageTitle(authUser)} / App Notifications`}
          isReadyToAudit={!!authUser}
          onClose={() => setConfigurationGroup(ConfigurationGroup.Root)}
        >
          <EditNotificationConfiguration
            onClose={() => setConfigurationGroup(ConfigurationGroup.Root)}
            licensedAgencyId={currentLicensedAgencyId}
          />
        </SidePanelContent>
      )}
    </React.Fragment>
  );
}

export const ConfigurationPage: PageDefinition = {
  routes: (user) => {
    const availableRoutes = []

    // Agency users don't use the full URL with IDs
    if (!isAssisting(user)) {
      availableRoutes.push('/configuration')
      availableRoutes.push('/configuration/devices')
      return availableRoutes
    }

    availableRoutes.push(
      '/assist/customer/:customerAgencyId/agency/:licensedAgencyId/configuration',
    )
    availableRoutes.push(
      '/assist/customer/:customerAgencyId/agency/:licensedAgencyId/configuration/devices',
    )
    return availableRoutes
  },
  type: [PageType.Agency],
  groupName: 'Configuration',
  sortOrder: () => 0,
  component: <Configuration />,
  navLinkText: 'App Settings',
  pageTitle: buildPageTitle,
  canAccess: canViewAgencyPage,
  buildNavLink: (user) => {
    return buildNavigationUrl(ConfigurationGroup.Root, isAssisting(user), {
      customerAgencyId: user?.customerAgencyId as string,
      licensedAgencyId: user?.licensedAgencyId as string,
    })
  },
}
